/**
 * packages/shared/src/scoring/confidence-tier.ts
 *
 * Duration-aware confidence tier (RUF-552, design D5 -
 * docs/prds/digital-passport-health-confidence.md). Metric coverage
 * (telemetry-coverage.ts) measures completeness only and is duration-blind -
 * 25 metrics × 2 days looks identical to 25 metrics × 2 years. That's the
 * laundering hole D5 closes: confidence tier = min(coverage tier, duration
 * tier), so a thin history can never outrank its actual observation span.
 *
 * Duration tiers bucket the asset's observed telemetry_daily_agg span
 * (earliest day to latest day - the FULL lifetime span, not the 30-day
 * scoring window): >=180d full, 30-180d partial, <30d minimal.
 */

import type { CoverageTier } from './telemetry-coverage.js';

export type DurationTier = 'full' | 'partial' | 'minimal';
export type ConfidenceTier = 'full' | 'partial' | 'minimal';

export const DURATION_TIER_FULL_DAYS = 180;
export const DURATION_TIER_PARTIAL_DAYS = 30;

/**
 * Bucket an observed telemetry_daily_agg span (inclusive day count between
 * the asset's earliest and latest reported day) into a duration tier.
 */
export function durationTierFromSpanDays(spanDays: number): DurationTier {
  if (spanDays >= DURATION_TIER_FULL_DAYS) return 'full';
  if (spanDays >= DURATION_TIER_PARTIAL_DAYS) return 'partial';
  return 'minimal';
}

/** Convenience overload: derive spanDays from the asset's first/last observed day. */
export function durationTierFromDates(firstDay: Date, lastDay: Date): DurationTier {
  const spanMs = lastDay.getTime() - firstDay.getTime();
  const spanDays = Math.floor(spanMs / (24 * 60 * 60 * 1000)) + 1; // inclusive both ends
  return durationTierFromSpanDays(Math.max(0, spanDays));
}

export const TIER_ORDER: readonly ConfidenceTier[] = ['minimal', 'partial', 'full'];

const TIER_RANK: Readonly<Record<CoverageTier | DurationTier, number>> = Object.freeze({
  minimal: 0,
  partial: 1,
  full: 2,
});

/**
 * Confidence tier = min(coverage tier, duration tier) (D5). Rank-ordered
 * minimal < partial < full so combining always yields the weaker signal -
 * good coverage over a thin history reads as thin, and vice versa.
 */
export function confidenceTier(coverage: CoverageTier, duration: DurationTier): ConfidenceTier {
  return TIER_RANK[coverage] <= TIER_RANK[duration] ? coverage : duration;
}

/**
 * SIL-150 - measured-vs-modeled confidence boost. Extends the existing D5
 * min(coverage, duration) mechanism rather than inventing a parallel one:
 * a live source (Datadog) is stronger evidence per-field than a
 * model-derived default even when it hasn't been reporting long enough to
 * move the duration tier, so a sufficiently-measured asset gets bumped one
 * tier above its plain coverage/duration result (capped at 'full').
 *
 * `measuredWeightFraction` = (Σ fieldWeight of v5 rules resolved from a
 * live/Datadog source within the freshness window) / (Σ fieldWeight of all
 * v5 rules) - see telemetry-source-precedence.ts's measuredNames output.
 * "Connecting a feed raises it": once enough live-measured field weight is
 * present the tier steps up. "Disconnecting decays it": measuredNames (and
 * therefore this fraction) is itself freshness-windowed upstream - once the
 * feed goes quiet for FIELD_SOURCE_FRESHNESS_WINDOW_DAYS, measuredNames
 * empties out on its own and the next scoring run naturally drops back to
 * the plain confidenceTier() result. No separate decay timer needed here.
 */
export const MEASURED_BOOST_MIN_WEIGHT_FRACTION = 0.15;

export function applyMeasuredBoost(
  tier: ConfidenceTier,
  measuredWeightFraction: number,
): ConfidenceTier {
  if (!Number.isFinite(measuredWeightFraction)) return tier;
  if (measuredWeightFraction < MEASURED_BOOST_MIN_WEIGHT_FRACTION) return tier;
  const idx = TIER_ORDER.indexOf(tier);
  return TIER_ORDER[Math.min(idx + 1, TIER_ORDER.length - 1)] as ConfidenceTier;
}

const CONFIDENCE_TIER_LABEL: Readonly<Record<ConfidenceTier, string>> = Object.freeze({
  full: 'Full History',
  partial: 'Partial History',
  minimal: 'Limited History',
});

/** Passport-facing label (D5): Full History / Partial History / Limited History. */
export function confidenceTierLabel(tier: ConfidenceTier): string {
  return CONFIDENCE_TIER_LABEL[tier];
}
